import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout, PageTransition, useModal } from '../../components/layout';
import { useProfileManagement } from '../../components/auth/useProfileManagement';
import { FileList } from '../../components/data-sources/file-upload/FileList';
import FileUpload from '../../components/data-sources/file-upload/FileUpload';
import MinioSettings from '../../components/data-sources/settings/MinioSettings';
import { API_ENDPOINTS } from '../../config/api.config';
import type { ProfileData } from '../../types/auth';
import { apiService } from '../../services/apiService';

interface FileListResponse {
    total: number;
    folders?: Array<{
        id: string;
        name: string;
        is_root: boolean;
    }>;
    rootFolder?: {
        id: string;
    };
}

const UploadedDriveFiles: React.FC = () => {
    useNavigate();
    const { user } = useAuth();
    const modalState = useModal();
    const [refreshKey, setRefreshKey] = useState(0);
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
    const [profileData, setProfileData] = useState<ProfileData>({
        fullName: user?.fullName || '',
        email: user?.email || ''
    });

    const profileManagement = useProfileManagement(
        undefined,
        modalState.setError,
        modalState.setSuccess,
        modalState.setIsLoading,
        modalState.setShowProfileModal,
        modalState.setShowPasswordModal
    );

    const fetchFiles = useCallback(async (params: {
        currentPage: number;
        itemsPerPage: number;
        searchTerm: string;
        source: string;
        sort: { field: string; direction: 'asc' | 'desc' };
        currentFolderId: string | null;
        rootFolderId: string | null;
    }) => {
        try {
            const data = await apiService.get<FileListResponse>(API_ENDPOINTS.DATA_SOURCE.LIST);
            return {
                total: data.total,
                totalPages: Math.ceil(data.total / params.itemsPerPage)
            };
        } catch (error) {
            console.error('Error fetching files:', error);
            return { total: 0, totalPages: 0 };
        }
    }, []);

    const fetchFolders = useCallback(async (folderId: string | null) => {
        try {
            const url = `${API_ENDPOINTS.DATA_SOURCE.LIST_FOLDERS}${folderId ? `?parent_id=${folderId}` : ''}`;
            return await apiService.get<FileListResponse>(url);
        } catch (error) {
            console.error('Error fetching folders:', error);
        }
    }, []);

    // Initialize root folder
    useEffect(() => {
        const fetchRootFolder = async () => {
            try {
                const data = await apiService.get<FileListResponse>(
                    `${API_ENDPOINTS.DATA_SOURCE.LIST_FOLDERS}?root=true`
                );

                // Try to get the root folder id either from rootFolder or by finding the home folder
                const rootId = data.rootFolder?.id ||
                    data.folders?.find((f) => f.is_root && f.name.toLowerCase() === 'home')?.id;

                if (rootId) {
                    setCurrentFolderId(rootId);
                }
            } catch (error) {
                console.error('Error fetching root folder:', error);
                modalState.setError('Failed to initialize folder structure');
            }
        };

        void fetchRootFolder();
    }, [modalState]);

    const handleUploadSuccess = useCallback(async () => {
        modalState.setSuccess('File uploaded successfully');
        setRefreshKey(prev => prev + 1);
    }, [modalState]);
    if (!user) {
        return (
            <PageTransition>
                <div className="container mt-5">
                    <div className="alert alert-warning">
                        Please log in to view files.
                    </div>
                </div>
            </PageTransition>
        );
    }

    return (
        <MainLayout
            user={user}
            modalProps={{
                profileData,
                error: modalState.error,
                success: modalState.success,
                isLoading: modalState.isLoading,
                showProfileModal: modalState.showProfileModal,
                handleProfileUpdate: (e: React.FormEvent) =>
                    profileManagement.handleProfileUpdate(e, profileData),
                handleDataChange: (data: Partial<ProfileData>) => {
                    if ('fullName' in data || 'email' in data) {
                        setProfileData(prev => ({ ...prev, ...data }));
                    }
                },
                setShowProfileModal: modalState.setShowProfileModal
            }}
        >
            <PageTransition>
                <div className="container-fluid py-4">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="card-title mb-0">My Uploaded Files</h5>
                                        <MinioSettings user={user} />
                                    </div>
                                    <div className="mb-4">
                                        <FileUpload
                                            onUploadSuccess={handleUploadSuccess}
                                            acceptedFileTypes=".txt,.pdf,.docx,.md,.csv,.json,.xlsx,.xls,.pptx,.ppt"
                                            maxFileSize={10 * 1024 * 1024}
                                            currentFolderId={currentFolderId}
                                            fetchFiles={fetchFiles}
                                            fetchFolders={fetchFolders}
                                            currentPage={1}
                                            itemsPerPage={10}
                                            searchTerm=""
                                            source="all"
                                            sort={{ field: 'updated_at', direction: 'desc' }}
                                            rootFolderId={currentFolderId}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <FileList key={refreshKey} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </PageTransition>
        </MainLayout>
    );
};

export default UploadedDriveFiles;